// src/components/MarqueeText.js
import { useRef, useState, useLayoutEffect } from "react";
import "../styles/MarqueeText.css";

/**
 * MarqueeText — keeps text on a single line and auto-scrolls it (marquee) only
 * when it's actually too wide to fit its container. When it fits, it renders a
 * plain line (with an ellipsis fallback). This mirrors the auto-scrolling track
 * names on the Song Requests page.
 *
 * Rendered as a <span> (display:block) so it's valid inside a <p>.
 *
 * @param {string}  text       the text to show (already display-cleaned)
 * @param {object}  style      styles for the clipping viewport (width/height)
 * @param {object}  textStyle  styles for the text itself (e.g. fontWeight)
 * @param {number}  gap        px gap between the two scrolling copies
 * @param {number}  speed      scroll speed in px/sec (higher = faster)
 */
export default function MarqueeText({
  text = "",
  style = {},
  textStyle = {},
  gap = 28,
  speed = 45,
}) {
  const viewportRef = useRef(null);
  const measureRef = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [duration, setDuration] = useState(8);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const measure = measureRef.current;
    if (!viewport || !measure) return;

    const evaluate = () => {
      const contentWidth = measure.scrollWidth;
      const boxWidth = viewport.clientWidth;
      const isOverflowing = contentWidth > boxWidth + 1;
      setOverflow(isOverflowing);
      if (isOverflowing) {
        // One loop travels one copy + gap; keep px/sec roughly constant so
        // short-ish overflows don't crawl and very long ones aren't a blur.
        const travel = contentWidth + gap;
        setDuration(Math.min(30, Math.max(6, travel / speed)));
      }
    };

    evaluate();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(evaluate);
      ro.observe(viewport);
      ro.observe(measure);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, [text, gap, speed]);

  return (
    <span ref={viewportRef} className="mb-marquee-viewport" style={{ textAlign: "left", ...style }}>
      {/* Hidden measurer — natural width of a single copy. */}
      <span ref={measureRef} className="mb-marquee-measure" style={textStyle} aria-hidden="true">
        {text}
      </span>

      {overflow ? (
        <span className="mb-marquee-track" style={{ animationDuration: `${duration}s` }}>
          <span style={{ ...textStyle, paddingRight: gap }}>{text}</span>
          <span style={{ ...textStyle, paddingRight: gap }} aria-hidden="true">
            {text}
          </span>
        </span>
      ) : (
        <span className="mb-marquee-static" style={{ textAlign: "left", ...textStyle }}>
          {text}
        </span>
      )}
    </span>
  );
}
